// CORE
import { executeRequest, type ApiResult } from '@/core/either';
import { http } from '@/core/api';

// TYPES
import type { CurrencyWalletType } from '@/types/CurrencyWalletType';
import type { AssociateCurrencyPayloadType } from '@/types/services/AssociateCurrencyPayloadType';
import type { IDestinationContactWallet, WalletType } from '@/types/WalletType';

export type CreateWalletResponse = {
  walletId: number;
};

export const walletService = {
  /**
   * Obtiene las monedas disponibles para la creación de nuevas billeteras.
   * @param type - Tipo opcional de filtro de monedas.
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  getWalletCurrencies(type?: string): Promise<ApiResult<CurrencyWalletType[]>> {
    return executeRequest(() =>
      http.get<CurrencyWalletType[]>('wallet-b2b/b2b/wallets/currencies', {
        params: { type },
      })
    );
  },

  /**
   * Asocia una moneda a la empresa para habilitar operaciones con esa moneda.
   * @param payload - Información requerida para la asociación de la moneda.
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  associateCurrency(
    payload: AssociateCurrencyPayloadType
  ): Promise<ApiResult<CreateWalletResponse>> {
    return executeRequest(() =>
      http.post<CreateWalletResponse>(
        'wallet-b2b/b2b/associate-currencies',
        payload
      )
    );
  },

  /**
   * Asocia una moneda estable (stablecoin) a la empresa.
   * @param currencyCode - Código de la stablecoin (ej: USDT, USDC).
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  associateStablecoin(currencyCode: string): Promise<ApiResult<CreateWalletResponse>> {
    return executeRequest(() =>
      http.post<CreateWalletResponse>(
        'wallet-b2b/b2b/associate-currencies/stablecoin',
        { currencyCode }
      )
    );
  },

  /**
   * Obtiene las cuentas de un contacto P2P de la empresa.
   * El backend responde 404 si el `beneficiaryId` no existe como contacto P2P de la empresa.
   * @param beneficiaryId - `beneficiaryId` del contacto P2P de la empresa, no su `customerId`.
   * @returns Promesa con el resultado tipado usando Either (ApiResult<IDestinationContactWallet[]>).
   */
  getContactWallets(beneficiaryId: number): Promise<ApiResult<IDestinationContactWallet[]>> {
    return executeRequest(() =>
      http.get<IDestinationContactWallet[]>(
        `beneficiary/b2b/p2p/${beneficiaryId}/accounts`
      )
    );
  },

  /**
   * Lista de wallets del usuario autenticado.
   * @returns ApiResult con el array de wallets del usuario
   */
  getWallets(): Promise<ApiResult<WalletType[]>> {
    return executeRequest(() =>
      http.get<WalletType[]>('wallet-b2b/b2b/wallets')
    );
  },
};
